import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import type { EventRecord } from '../../shared/contracts';
import { sourceMailResolverLink } from '../../domain/gmail-link';
import { api } from '../api';
import { EventCard } from '../components/EventCard';
import { EditDialog, IgnoreSourceDialog } from '../components/EventDialogs';
import type { ProcessResponse } from '../types';

interface MailSearchMessage {
  id: string;
  threadId: string;
  sender: string;
  senderEmail: string;
  subject: string;
  receivedAt: string;
  labelIds: string[];
  snippet: string;
  events: EventRecord[];
}

function mailboxLabel(labels: string[]): string {
  if (labels.includes('TRASH')) return 'メールはゴミ箱';
  if (labels.includes('SPAM')) return 'メールは迷惑メール';
  if (labels.includes('INBOX')) return 'メールは受信トレイ';
  return 'メールはアーカイブ';
}

function scheduleInfo(events: EventRecord[]): { label: string; className: string } {
  if (events.some((event) => !event.deleted && !event.calendarEventId)) return { label: 'カレンダー未登録の確認候補あり', className: 'registered' };
  if (events.some((event) => !event.deleted && !event.archived)) return { label: '予定はカレンダー登録済み', className: 'registered' };
  if (events.some((event) => !event.deleted && event.archived && event.archiveCategory === 'confirmed')) return { label: '予定は確認済み', className: 'confirmed-done' };
  if (events.some((event) => !event.deleted && event.archived)) return { label: '予定は保管済み', className: 'archived' };
  if (events.some((event) => event.deleted)) return { label: '予定は削除済み', className: 'deleted' };
  return { label: '予定は未登録', className: '' };
}

export function MailSearchPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedQuery = searchParams.get('q')?.trim() ?? '';
  const [query, setQuery] = useState(requestedQuery);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [messages, setMessages] = useState<MailSearchMessage[]>([]);
  const [searching, setSearching] = useState(false);
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const [archiving, setArchiving] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<EventRecord | null>(null);
  const [ignoring, setIgnoring] = useState<EventRecord | null>(null);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const deleteRef = useRef(new Set<string>());
  const archiveRef = useRef(new Set<string>());

  const fetchResults = useCallback(async (trimmed: string) => {
    setSearching(true); setError(''); setNotice('');
    try {
      const result = await api<{ messages: MailSearchMessage[] }>(`/api/gmail/search?q=${encodeURIComponent(trimmed)}`);
      setMessages(result.messages); setSearchedQuery(trimmed);
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'メールを検索できませんでした'); }
    finally { setSearching(false); }
  }, []);
  useEffect(() => {
    if (!requestedQuery) return;
    setQuery(requestedQuery);
    void fetchResults(requestedQuery);
  }, [fetchResults, requestedQuery]);
  const search = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    if (trimmed === requestedQuery) void fetchResults(trimmed);
    else setSearchParams({ q: trimmed });
  };
  const updateEvent = (id: string, change: (event: EventRecord) => EventRecord) => setMessages((current) => current.map((message) => ({
    ...message, events: message.events.map((event) => event.id === id ? change(event) : event),
  })));
  const remove = async (id: string) => {
    if (deleteRef.current.has(id)) return;
    deleteRef.current.add(id); setDeleting((current) => new Set([...current, id])); setError('');
    try {
      const result = await api<{ deleted: number }>('/api/events/bulk-delete', { method: 'POST', body: JSON.stringify({ ids: [id] }) });
      if (result.deleted !== 1) throw new Error('予定を削除できませんでした');
      updateEvent(id, (event) => ({ ...event, deleted: true, archived: false, status: 'deleted' }));
      setNotice('予定を削除しました。元メールは削除していません。');
    } catch (reason) { setError(reason instanceof Error ? reason.message : '予定を削除できませんでした'); }
    finally { deleteRef.current.delete(id); setDeleting((current) => { const next = new Set(current); next.delete(id); return next; }); }
  };
  const archive = async (id: string) => {
    if (archiveRef.current.has(id)) return;
    archiveRef.current.add(id); setArchiving((current) => new Set([...current, id])); setError('');
    try {
      const result = await api<{ archived: number }>('/api/events/bulk-archive', { method: 'POST', body: JSON.stringify({ ids: [id], category: 'confirmed' }) });
      if (result.archived !== 1) throw new Error('予定を確認して残す処理ができませんでした');
      updateEvent(id, (event) => ({ ...event, archived: true, archiveCategory: 'confirmed', status: 'archived' }));
      setNotice('予定を確認して残しました。');
    } catch (reason) { setError(reason instanceof Error ? reason.message : '予定を確認して残す処理ができませんでした'); }
    finally { archiveRef.current.delete(id); setArchiving((current) => { const next = new Set(current); next.delete(id); return next; }); }
  };
  const unarchive = async (id: string) => {
    setArchiving((current) => new Set([...current, id]));
    try {
      const result = await api<{ event: EventRecord }>(`/api/events/${id}/unarchive`, { method: 'POST' });
      updateEvent(id, () => result.event); setNotice('予定を通常の一覧へ戻しました。');
    } finally { setArchiving((current) => { const next = new Set(current); next.delete(id); return next; }); }
  };
  const restore = async (id: string) => {
    setProcessing((current) => new Set([...current, id]));
    try {
      const result = await api<{ event: EventRecord }>(`/api/events/${id}/restore`, { method: 'POST' });
      updateEvent(id, () => result.event); setNotice('予定をカレンダーへ戻しました。');
    } finally { setProcessing((current) => { const next = new Set(current); next.delete(id); return next; }); }
  };
  const processMessage = async (messageId: string) => {
    setProcessing((current) => new Set([...current, messageId])); setError(''); setNotice('');
    try {
      const result = await api<ProcessResponse>(`/api/gmail/messages/${encodeURIComponent(messageId)}/process`, { method: 'POST' });
      if (result.summary.errors) throw new Error('このメールから予定を作れませんでした');
      await fetchResults(searchedQuery || query.trim());
      setNotice(result.summary.created ? `${result.summary.created}件の予定・確認候補を処理しました。` : 'このメールには登録できる日付が見つかりませんでした。');
    } catch (reason) { setError(reason instanceof Error ? reason.message : '予定として確認できませんでした'); }
    finally { setProcessing((current) => { const next = new Set(current); next.delete(messageId); return next; }); }
  };
  const ignoreSource = async (pattern: string, field: 'subject' | 'email') => {
    if (!ignoring) return;
    setDialogBusy(true); setError('');
    try {
      await api(`/api/events/${ignoring.id}/ignore-source`, { method: 'POST', body: JSON.stringify({ pattern, field }) });
      setIgnoring(null); setNotice('今後拾わない条件を追加しました。'); await fetchResults(searchedQuery);
    } catch (reason) { setError(reason instanceof Error ? reason.message : '条件を追加できませんでした'); }
    finally { setDialogBusy(false); }
  };

  return <div className="page mail-search-page">
    <section className="page-title"><h1>メール検索</h1></section>
    <form className="mail-search-form" onSubmit={(event) => { event.preventDefault(); search(); }}>
      <label><span className="sr-only">メールの検索語</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="送信者、件名、本文の言葉" maxLength={200} /></label>
      <button className="button primary" disabled={searching || !query.trim()}>{searching ? '検索中…' : 'メールを検索'}</button>
    </form>
    <p className="search-help">例：「富山商工会議所」「請求書」「from:example.com」</p>
    {error && <div className="alert error">{error}</div>}
    {notice && <div className="alert success" role="status">{notice}</div>}
    {searchedQuery && <div className="search-result-heading"><strong>「{searchedQuery}」の検索結果</strong><span>{messages.length}通</span></div>}
    <div className="mail-search-results">{messages.map((message) => { const schedule = scheduleInfo(message.events); return <article className="mail-result" key={message.id}>
      <div className="mail-result-meta"><span>{mailboxLabel(message.labelIds)}</span><span className={schedule.className}>{schedule.label}</span><time>{new Date(message.receivedAt).toLocaleString('ja-JP')}</time></div>
      <h2>{message.subject}</h2><p className="sender">{message.sender}</p>{message.snippet && <p className="mail-snippet">{message.snippet}</p>}
      <div className="mail-result-actions"><Link className="button secondary" to={sourceMailResolverLink(message.id)} state={{ sourceReturnTo: `${location.pathname}${location.search}`, sourceMailFallback: {
        subject: message.subject, sender: message.sender, threadId: message.threadId,
        receivedAt: message.receivedAt, sourceText: message.snippet,
      } }}>元メールを開く</Link>{message.events.length === 0 && <button className="button primary" disabled={processing.has(message.id)} onClick={() => void processMessage(message.id)}>{processing.has(message.id) ? '確認中…' : 'AIで予定を拾う'}</button>}</div>
      {message.events.length > 0 && <div className="linked-events"><strong>このメールから作った予定</strong>{message.events.map((event) => <EventCard key={event.id} event={event} selectable={false} selected={false} deleting={deleting.has(event.id)} archiving={archiving.has(event.id) || processing.has(event.id)} onSelect={() => undefined} onDelete={remove} onArchive={archive} onUnarchive={unarchive} onIgnoreSource={setIgnoring} onRestore={restore} onEdit={setEditing} />)}</div>}
    </article>; })}</div>
    {searchedQuery && messages.length === 0 && !searching && <div className="empty-state"><span>⌕</span><h3>メールが見つかりませんでした</h3><p>短い言葉や送信者名でもう一度お試しください。</p></div>}
    {editing && <EditDialog event={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); setNotice('予定を変更しました。'); await fetchResults(searchedQuery); }} />}
    {ignoring && <IgnoreSourceDialog event={ignoring} busy={dialogBusy} onClose={() => !dialogBusy && setIgnoring(null)} onConfirm={ignoreSource} />}
  </div>;
}
